import React, { useState, useEffect } from "react";
import reportAPI from "../../../../api/report";
import { FilterYearMonthType } from "../common/ReportFilters";
import { ButtonExcel } from "../../../../utils/buttonLoadExcel";
import { BarChart, Bar, YAxis, CartesianGrid, Tooltip } from "recharts";
import {
  BARCHART,
  FLEXDROPDOWN,
  SCREENIPAD,
  STYLEBODY,
} from "../../../../utils/useStyle";
import { ExcelConfrimation } from "../../functions/Report.ExportConfirmation";
import { formatConfrimation } from "../../utils/Report.Format";

const ReportConfirmation = ({ userDept }) => {
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState({
    label: "ทุกปี",
    value: "ALL",
  });
  const [month, setMonth] = useState({
    label: "ทุกเดือน",
    value: "ALL",
  });
  const [type, setType] = useState({
    label: "-",
    value: "NONE",
  });

  const [data, setData] = React.useState({
    po_1: 0,
    po_2: 0,
    po_3: 0,
    po_4: 0,
    po_5: 0,
    po_6: 0,
    po_7: 0,
    po_8: 0,
    // in_ivc/no_ivc are now mapped from API response
    in_ivc: 0,
    no_ivc: 0,
    total_po: 0,
  });

  const [data2, setData2] = React.useState({
    con_good_flu: 0,
    con_good_iecg: 0,
    con_good_land: 0,
    
    con_short_flu: 0,
    con_short_iecg: 0,
    con_short_land: 0,

    con_long_flu: 0,
    con_long_iecg: 0,
    con_long_land: 0,

    con_un_flu: 0,
    con_un_iecg: 0,
    con_un_land: 0,
    con_un_miss: 0,
    
    miss_upper: 0,
    miss_lower: 0,

    in_ivc_iecg: 0,
    no_ivc_iecg: 0,

    in_ivc_flu: 0,
    no_ivc_flu: 0,

    in_ivc_land: 0,
    no_ivc_land: 0,

    in_ivc_miss: 0,
    no_ivc_miss: 0,

    lower_unflu: 0,
    lower_uniecg: 0,
    lower_unland: 0,
    lower_unmiss: 0,

    total_lower_un: 0,
  });

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await reportAPI.getConfirmationReport({
          year: String(year?.value),
          type: String(type?.value),
          month: String(month?.value),
          user_department: "all", // Or pass userDept prop if available
        });
        
        if (response?.success) {
          setData(response.data);
          setData2(response.data2);
        }
      } catch (error) {
        console.error("Error fetching confirmation report:", error);
      }
    };
    fetchData();
  }, [year, type, month, userDept]);

  const listTable1 = [
    { name: "-3", count: data?.po_1 },
    { name: "-2", count: data?.po_2 },
    { name: "-1", count: data?.po_3 },
    { name: "0", count: data?.po_4 },
    { name: "1", count: data?.po_5 },
    { name: "2", count: data?.po_6 },
    { name: "3", count: data?.po_7 },
    { name: "In IVC", count: data?.in_ivc },
    { name: "Not in IVC", count: data?.no_ivc },
    { name: "Unintended Position", count: data?.po_8 },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const count = payload[0].value;
      const total = listTable1.reduce((a, b) => a + b.count, 0);
      const percentage = ((count / total) * 100).toFixed(1);
      return (
        <div className="bg-gray-50 text-gray-700 text-sm px-2 py-1 rounded">
          <p>{`${count} (${percentage}%)`}</p>
        </div>
      );
    }
    return null;
  };

  let totalIECG = 0;
  let totalLandmark = 0;
  let totalFluoroscopy = 0;
  let totalMissingData = 0;
  let totalLowerIECG = 0;
  let totalLowerLandmark = 0;
  let totalLowerFluroscopy = 0;

  if (data2) {
    totalIECG =
      (data2?.con_good_iecg || 0) +
      (data2?.con_short_iecg || 0) +
      (data2?.con_long_iecg || 0) +
      (data2?.con_un_iecg || 0);
    totalLandmark =
      (data2?.con_good_land || 0) +
      (data2?.con_short_land || 0) +
      (data2?.con_long_land || 0) +
      (data2?.con_un_land || 0);
    totalFluoroscopy =
      (data2?.con_good_flu || 0) +
      (data2?.con_short_flu || 0) +
      (data2?.con_long_flu || 0) +
      (data2?.con_un_flu || 0);
    totalMissingData = data2.con_un_miss || 0;
    totalLowerIECG =
      data2?.lower_uniecg + data2?.in_ivc_iecg + data2?.no_ivc_iecg;
    totalLowerLandmark =
      data2?.lower_unland + data2?.in_ivc_land + data2?.no_ivc_land;
    totalLowerFluroscopy =
      data2?.lower_unflu + data2?.in_ivc_flu + data2?.no_ivc_flu;
  }

  const IECGpercentGood =
    totalIECG === 0 ? 0 : ((data2?.con_good_iecg * 100) / totalIECG).toFixed(1);
  const IECGpercentShort =
    totalIECG === 0
      ? 0
      : ((data2?.con_short_iecg * 100) / totalIECG).toFixed(1);
  const IECGpercentLong =
    totalIECG === 0 ? 0 : ((data2?.con_long_iecg * 100) / totalIECG).toFixed(1);
  const IECGpercentUn =
    totalIECG === 0 ? 0 : ((data2?.con_un_iecg * 100) / totalIECG).toFixed(1);

  const LandmarkpercentGood =
    totalLandmark === 0
      ? 0
      : ((data2?.con_good_land * 100) / totalLandmark).toFixed(1);
  const LandmarkpercentShort =
    totalLandmark === 0
      ? 0
      : ((data2?.con_short_land * 100) / totalLandmark).toFixed(1);
  const LandmarkpercentLong =
    totalLandmark === 0
      ? 0
      : ((data2?.con_long_land * 100) / totalLandmark).toFixed(1);
  const LandmarkpercentUn =
    totalLandmark === 0
      ? 0
      : ((data2?.con_un_land * 100) / totalLandmark).toFixed(1);

  const FluoroscopypercentGood =
    totalFluoroscopy === 0
      ? 0
      : ((data2?.con_good_flu * 100) / totalFluoroscopy).toFixed(1);
  const FluoroscopypercentShort =
    totalFluoroscopy === 0
      ? 0
      : ((data2?.con_short_flu * 100) / totalFluoroscopy).toFixed(1);
  const FluoroscopypercentLong =
    totalFluoroscopy === 0
      ? 0
      : ((data2?.con_long_flu * 100) / totalFluoroscopy).toFixed(1);
  const FluoroscopypercentUn =
    totalFluoroscopy === 0
      ? 0
      : ((data2?.con_un_flu * 100) / totalFluoroscopy).toFixed(1);

  const LowerPercentInIvcIECG =
    totalLowerIECG === 0
      ? 0
      : ((data2?.in_ivc_iecg * 100) / totalLowerIECG).toFixed(1);
  const LowerPercentNoIvcIECG =
    totalLowerIECG === 0
      ? 0
      : ((data2?.no_ivc_iecg * 100) / totalLowerIECG).toFixed(1);
  const LowerPercentUnIvcIECG =
    totalLowerIECG === 0
      ? 0
      : ((data2?.lower_uniecg * 100) / totalLowerIECG).toFixed(1);

  const LowerPercentInIvcLandmark =
    totalLowerLandmark === 0
      ? 0
      : ((data2?.in_ivc_land * 100) / totalLowerLandmark).toFixed(1);
  const LowerPercentNoIvcLandmark =
    totalLowerLandmark === 0
      ? 0
      : ((data2?.no_ivc_land * 100) / totalLowerLandmark).toFixed(1);
  const LowerPercentUnIvcLandmark =
    totalLowerLandmark === 0
      ? 0
      : ((data2?.lower_unland * 100) / totalLowerLandmark).toFixed(1);

  const LowerPercentInIvcFluroscopy =
    totalLowerFluroscopy === 0
      ? 0
      : ((data2?.in_ivc_flu * 100) / totalLowerFluroscopy).toFixed(1);
  const LowerPercentNoIvcFluroscopy =
    totalLowerFluroscopy === 0
      ? 0
      : ((data2?.no_ivc_flu * 100) / totalLowerFluroscopy).toFixed(1);
  const LowerPercentUnIvcFluroscopy =
    totalLowerFluroscopy === 0
      ? 0
      : ((data2?.lower_unflu * 100) / totalLowerFluroscopy).toFixed(1);

  const mockData = [
    {
      col1IECG: "iECG",
      col2IECG: `${totalIECG}  `,
      col3IECG: `${data2?.con_good_iecg} (${IECGpercentGood}%)`,
      col4IECG: `${data2?.con_short_iecg} (${IECGpercentShort}%)`,
      col5IECG: `${data2?.con_long_iecg} (${IECGpercentLong}%) `,
      col6IECG: `${data2?.con_un_iecg} (${IECGpercentUn}%) `,
      col1Landmark: "Landmark",
      col2Landmark: `${totalLandmark}  `,
      col3Landmark: `${data2?.con_good_land} (${LandmarkpercentGood}%)`,
      col4Landmark: `${data2?.con_short_land} (${LandmarkpercentShort}%)`,
      col5Landmark: `${data2?.con_long_land} (${LandmarkpercentLong}%) `,
      col6Landmark: `${data2?.con_un_land} (${LandmarkpercentUn}%) `,
      col1Fluoroscopy: "Fluoroscopy",
      col2Fluoroscopy: `${totalFluoroscopy}  `,
      col3Fluoroscopy: `${data2?.con_good_flu} (${FluoroscopypercentGood}%)`,
      col4Fluoroscopy: `${data2?.con_short_flu} (${FluoroscopypercentShort}%)`,
      col5Fluoroscopy: `${data2?.con_long_flu} (${FluoroscopypercentLong}%) `,
      col6Fluoroscopy: `${data2?.con_un_flu} (${FluoroscopypercentUn}%) `,
      col1MissingUpper: "Missing Upper",
      col2MissingUpper: `${data2?.miss_upper}  `,
    },
  ];

  const mockData2 = [
    {
      col1IECG: "iECG",
      col2IECG: `${totalLowerIECG}`,
      col3IECG: `${data2?.in_ivc_iecg} (${LowerPercentInIvcIECG}%) `,
      col4IECG: `${data2?.no_ivc_iecg} (${LowerPercentNoIvcIECG}%) `,
      col5IECG: `${data2?.lower_uniecg} (${LowerPercentUnIvcIECG}%) `,
      col1Landmark: "Landmark",
      col2Landmark: `${totalLowerLandmark}`,
      col3Landmark: `${data2?.in_ivc_land} (${LowerPercentInIvcLandmark}%) `,
      col4Landmark: `${data2?.no_ivc_land} (${LowerPercentNoIvcLandmark}%) `,
      col5Landmark: `${data2?.lower_unland} (${LowerPercentUnIvcLandmark}%) `,
      col1Fluoroscopy: "Fluoroscopy",
      col2Fluoroscopy: `${totalLowerFluroscopy}`,
      col3Fluoroscopy: `${data2?.in_ivc_flu} (${LowerPercentInIvcFluroscopy}%) `,
      col4Fluoroscopy: `${data2?.no_ivc_flu} (${LowerPercentNoIvcFluroscopy}%) `,
      col5Fluoroscopy: `${data2?.lower_unflu} (${LowerPercentUnIvcFluroscopy}%) `,
      col1MissingLower: "Missing Lower",
      col2MissingLower: `${data2?.miss_lower}  `,
    },
  ];

  const renderCustomLabel = (props) => {
    const { x, y, width, height, value } = props;
    const percentage = ((value / data?.total_po) * 100).toFixed(1) + "%";
    const insideBarPosition = y + height / 2;

    return (
      <g>
        <text
          x={x + width / 2}
          y={insideBarPosition}
          fill="#111827"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {`${value} (${percentage})`}
        </text>
      </g>
    );
  };

  return (
    <div className="">
      <div className={``}>
        <div className="my-10">
          <div>
            <div className="flex flex-col gap-4">
              <div className="text-black text-lg font-medium">
                รายงานตำแหน่งของสายหลังการถ่าย X-ray ปอด
                <div className={STYLEBODY}>
                  ของปี "{year.label}" ประเภทสาย "{type.label}"
                </div>
              </div>
              <div className={FLEXDROPDOWN}>
                <FilterYearMonthType
                  year={year}
                  setYear={setYear}
                  type={type}
                  setType={setType}
                  month={month}
                  setMonth={setMonth}
                />
                <ButtonExcel
                  onClick={() => {
                    ExcelConfrimation(
                      formatConfrimation(data),
                      year,
                      type,
                      setLoading
                    );
                  }}
                  loading={loading}
                />
              </div>
            </div>
            <div className={BARCHART}>
              <BarChart
                width={750}
                height={300}
                data={listTable1}
                margin={{
                  top: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Bar label={renderCustomLabel} dataKey="count" fill="#fde047" />
              </BarChart>
            </div>
            <div className="grid grid-cols-10 ml-16 mr-4 my-4 text-sm">
              <div className="text-center">-3</div>
              <div className="text-center">-2</div>
              <div className="text-center">-1</div>
              <div className="text-center">0</div>
              <div className="text-center">1</div>
              <div className="text-center">2</div>
              <div className="text-center">3</div>
              <div className="text-center">In IVC</div>
              <div className="text-center">Not in IVC</div>
              <div className="text-center">Unintended Position</div>
            </div>
            <div className="flex justify-center">
              <div className="flex items-center">
                <div className="box-content h-3 w-4 bg-yellowgraph"></div>
                <div className="text-yellowgraph mx-2">count</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Upper Torso Section */}
      <div className="mx-10 font-bold text-lg">
        Tip of catheter (Upper torso catheter){" "}
      </div>
      _____________________________________________________________________________________
      <div className={``}>
        <div className="my-2">
          <>
            <div className={SCREENIPAD}>
              <div>
                <div className="grid grid-cols-6 gap-3 border rounded p-2 bg-gray-100">
                  <div className="text-center">Confirmation technique</div>
                  <div className="text-center">Total (N)</div>
                  <div className="text-center">Proper position N (%)</div>
                  <div className="text-center">Too short N (%)</div>
                  <div className="text-center">Too deep N (%)</div>
                  <div className="text-center">Unintended position</div>
                </div>
                {mockData.map((item, index) => (
                  <div key={index}>
                    <div className="grid grid-cols-6 gap-3 px-2 py-1">
                      <div className="text-center">{item.col1IECG}</div>
                      <div className="text-center">{item.col2IECG}</div>
                      <div className="text-center">{item.col3IECG}</div>
                      <div className="text-center">{item.col4IECG}</div>
                      <div className="text-center">{item.col5IECG}</div>
                      <div className="text-center">{item.col6IECG}</div>
                    </div>
                    <div className="grid grid-cols-6 gap-3 px-2 py-1">
                      <div className="text-center">{item.col1Landmark}</div>
                      <div className="text-center">{item.col2Landmark}</div>
                      <div className="text-center">{item.col3Landmark}</div>
                      <div className="text-center">{item.col4Landmark}</div>
                      <div className="text-center">{item.col5Landmark}</div>
                      <div className="text-center">{item.col6Landmark}</div>
                    </div>
                    <div className="grid grid-cols-6 gap-3 px-2 py-1">
                      <div className="text-center">{item.col1Fluoroscopy}</div>
                      <div className="text-center">{item.col2Fluoroscopy}</div>
                      <div className="text-center">{item.col3Fluoroscopy}</div>
                      <div className="text-center">{item.col4Fluoroscopy}</div>
                      <div className="text-center">{item.col5Fluoroscopy}</div>
                      <div className="text-center">{item.col6Fluoroscopy}</div>
                    </div>
                    <div className="grid grid-cols-6 gap-3 px-2 py-1">
                      <div className="text-center">
                        {item?.col1MissingUpper}
                      </div>
                      <div className="text-center">
                        {item?.col2MissingUpper}
                      </div>
                    </div>
                  </div>
                ))}
                *<i> หมายเหตุ </i> Proper position (+2,+1,0,-1,-2) ,Too short
                (-3) , Too Deep (+3)
              </div>
            </div>
          </>
        </div>
      </div>
      {/* Lower Torso Section */}
      <div className="mx-10 font-bold text-lg">
        Tip of catheter (Lower torso catheter){" "}
      </div>
      _____________________________________________________________________________________
      <div className={``}>
        <>
          <div className={SCREENIPAD}>
            <div>
              <div className="grid grid-cols-5 gap-3 border rounded p-2 bg-gray-100">
                <div className="text-center">Confirmation technique</div>
                <div className="text-center">Total (N)</div>
                <div className="text-center">
                  In IVC <br />
                  N(%)
                </div>
                <div className="text-center">
                  Not in IVC <br /> N(%)
                </div>
                <div className="text-center">Unintended position</div>
              </div>
              {mockData2.map((item, index) => (
                <div key={index}>
                  <div className="grid grid-cols-5 gap-3 px-2 py-1">
                    <div className="text-center">{item.col1IECG}</div>
                    <div className="text-center">{item.col2IECG}</div>
                    <div className="text-center">{item.col3IECG}</div>
                    <div className="text-center">{item.col4IECG}</div>
                    <div className="text-center">{item.col5IECG}</div>
                  </div>
                  <div className="grid grid-cols-5 gap-3 px-2 py-1">
                    <div className="text-center">{item.col1Landmark}</div>
                    <div className="text-center">{item.col2Landmark}</div>
                    <div className="text-center">{item.col3Landmark}</div>
                    <div className="text-center">{item.col4Landmark}</div>
                    <div className="text-center">{item.col5Landmark}</div>
                  </div>
                  <div className="grid grid-cols-5 gap-3 px-2 py-1">
                    <div className="text-center">{item.col1Fluoroscopy}</div>
                    <div className="text-center">{item.col2Fluoroscopy}</div>
                    <div className="text-center">{item.col3Fluoroscopy}</div>
                    <div className="text-center">{item.col4Fluoroscopy}</div>
                    <div className="text-center">{item.col5Fluoroscopy}</div>
                  </div>
                  <div className="grid grid-cols-5 gap-3 px-2 py-1">
                    <div className="text-center">{item?.col1MissingLower}</div>
                    <div className="text-center">{item?.col2MissingLower}</div>
                  </div>
                  Missing Data : {totalMissingData}{" "}
                </div>
              ))}
            </div>
          </div>
        </>
      </div>
    </div>
  );
};

export default ReportConfirmation;
